import {
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";

import { usePrevious } from "@uidotdev/usehooks";
import { SidebarModalPanelV2 } from "@vega-core/components/Modals/SidebarModal/SidebarModalPanelV2";
import { SidebarModalRoot } from "@vega-core/components/Modals/SidebarModal/SidebarModalRoot";
import { Key } from "@vega-core/constants/global";
import { PageScrollPrevent } from "@vega-core/utils/ScrollPreventHelper";

const TRANSITION_TIME = 150;

type Props = {
  actionPanel?: ReactNode;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  noPadding?: boolean;
  noScrolling?: boolean;
  dataTestId: string;
  title?: string;
  headerAction?: ReactNode;
};

export function SidebarModalV2({
  actionPanel,
  children,
  isOpen,
  setIsOpen,
  noPadding = false,
  noScrolling = false,
  dataTestId = "",
  title,
  headerAction,
}: PropsWithChildren<Props>) {
  const [isClosing, setIsClosing] = useState(false);
  const isOpenPrev = usePrevious(isOpen);

  const handleClose = useCallback(() => {
    if (isClosing || !isOpen) return;

    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, TRANSITION_TIME);
  }, [isOpen, isClosing, setIsOpen, setIsClosing]);

  const onOpen = useCallback(() => {
    PageScrollPrevent.enable();
  }, []);

  const onCloseCleanup = useCallback(() => {
    PageScrollPrevent.disable();
  }, []);

  useEffect(() => {
    if (isOpen) {
      if (!isOpenPrev) {
        onOpen();
      }
    } else if (isOpenPrev) {
      onCloseCleanup();
    }

    return () => {
      if (isOpen) {
        onCloseCleanup();
      }
    };
  }, [isOpen, isOpenPrev, onOpen, onCloseCleanup]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === Key.Escape) {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, handleClose]);

  return (
    <SidebarModalRoot
      isClosing={isClosing}
      isOpen={isOpen}
      onClose={handleClose}
    >
      <SidebarModalPanelV2
        actionPanel={actionPanel}
        dataTestId={dataTestId}
        headerAction={headerAction}
        isClosing={isClosing}
        isOpen={isOpen}
        noPadding={noPadding}
        noScrolling={noScrolling}
        onClose={handleClose}
        title={title}
      >
        {children}
      </SidebarModalPanelV2>
    </SidebarModalRoot>
  );
}
